// Cryptographic hashing via the native Web Crypto API. SHA family only — MD5 is
// intentionally not offered (not supported by Web Crypto and cryptographically
// broken). All hashing runs locally; no data leaves the browser.

export type HashAlgo = 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512';

export const HASH_ALGOS: HashAlgo[] = ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'];

function toHex(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, '0');
  return s;
}

export async function hashBytes(algo: HashAlgo, bytes: Uint8Array): Promise<string> {
  // Uint8Array.from yields an ArrayBuffer-backed view (TS 5.7 typed-array generics).
  const digest = await crypto.subtle.digest(algo, Uint8Array.from(bytes));
  return toHex(digest);
}

export async function hashText(algo: HashAlgo, text: string): Promise<string> {
  return hashBytes(algo, new TextEncoder().encode(text));
}

/** Computes all supported algorithms for the given bytes. */
export async function hashAll(bytes: Uint8Array): Promise<Record<HashAlgo, string>> {
  const entries = await Promise.all(
    HASH_ALGOS.map(async (algo) => [algo, await hashBytes(algo, bytes)] as const),
  );
  return Object.fromEntries(entries) as Record<HashAlgo, string>;
}

export async function hashAllText(text: string): Promise<Record<HashAlgo, string>> {
  return hashAll(new TextEncoder().encode(text));
}
